import React from 'react';
import { GenericDialogue } from 'src/components/Dialogue/Dialogue';
import Button from 'src/components/Button/Button';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import NewPasswordFields from 'src/components/NewPasswordFields';
import PasswordChangeIllustration from 'src/components/PasswordChangeIllustration';
import {
  useChangePasswordDialogueOrchestrator,
  UseChangePasswordDialogueOrchestratorReturn,
} from './useChangePasswordDialogueOrchestrator';
import './change-password-dialogue.scss';

const ChangePasswordSuccess = () => (
  <div className="cpd-success">
    <PasswordChangeIllustration
      variant="success"
      className="cpd-success-icon"
    />
    <div className="cpd-success-heading">Password updated</div>
    <p className="cpd-success-body">
      Your password has been changed successfully.
    </p>
  </div>
);

interface ChangePasswordFooterProps {
  isSuccess: boolean;
  isSubmitting: boolean;
  canSubmit: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const ChangePasswordFooter = ({
  isSuccess,
  isSubmitting,
  canSubmit,
  onClose,
  onSubmit,
}: ChangePasswordFooterProps) => {
  if (isSuccess) {
    return <Button color="teal" size="small" label="Done" onClick={onClose} />;
  }

  return (
    <>
      <Button
        color="white"
        size="small"
        label="Cancel"
        onClick={onClose}
        disabled={isSubmitting}
      />
      <LoadingButton
        actionText="Update password"
        isSubmitting={isSubmitting}
        disabled={!canSubmit}
        onClick={onSubmit}
      />
    </>
  );
};

interface ChangePasswordDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  email: string;
}

const ChangePasswordDialogue = ({
  isOpen,
  onClose,
  userId,
  email,
}: ChangePasswordDialogueProps) => {
  const {
    step,
    oldPassword,
    password,
    confirmPassword,
    requirements,
    passwordsMismatch,
    canSubmit,
    updateOldPassword,
    updatePassword,
    updateConfirmPassword,
    submit,
    reset,
  }: UseChangePasswordDialogueOrchestratorReturn = useChangePasswordDialogueOrchestrator(userId, email);

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const isSubmitting: boolean = step === 'submitting';
  const isSuccess: boolean = step === 'success';

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={handleClose}
      title="Change password"
      description={
        isSuccess
          ? undefined
          : 'Enter your current password and choose a new one.'
      }
      maxWidth="70rem"
      minHeight="50rem"
      footer={
        <ChangePasswordFooter
          isSuccess={isSuccess}
          isSubmitting={isSubmitting}
          canSubmit={canSubmit}
          onClose={handleClose}
          onSubmit={submit}
        />
      }
    >
      {isSuccess ? (
        <ChangePasswordSuccess />
      ) : (
        <NewPasswordFields
          oldPassword={oldPassword}
          onOldPasswordChange={updateOldPassword}
          password={password}
          confirmPassword={confirmPassword}
          onPasswordChange={updatePassword}
          onConfirmPasswordChange={updateConfirmPassword}
          requirements={requirements}
          passwordsMismatch={passwordsMismatch}
        />
      )}
    </GenericDialogue>
  );
};

export default ChangePasswordDialogue;
